import { get, writable } from 'svelte/store';
import { createCartItemsStore } from '#lib/features/checkout/utils/createCartItemsStore/index.js';
import { createDialogStores } from '$global/components/Dialog';

export type WishlistContext = ReturnType<typeof createWishlistContext>;

// The wishlist is cross-drop, so it uses a single fixed storage key rather than
// the per-drop `cartSuffix` scoping used by the cart. Runtime localStorage keys
// are `primary-wishlist-cart-items` / `-cart-id` / `-code`.
export const WISHLIST_STORE_KEY = 'primary-wishlist';

export const createWishlistContext = () => {
	const wishlistItems = createCartItemsStore(WISHLIST_STORE_KEY);
	const wishlistDrawer = createDialogStores();

	// The heart icon is only shown when at least one wishlist-eligible drop is
	// currently active. Driven by the header on each time tick.
	const showWishlistIcon = writable(false);

	// Removes any wishlist item whose drop (keyed by the collection `code`, i.e.
	// `CartItem.groupId`) is no longer in the active set, so saved items disappear
	// as soon as their drop closes. `activeGroupIds` is the set of collection codes
	// of the currently active wishlist-eligible drops.
	const pruneInactiveDrops = (activeGroupIds: Set<string>) => {
		wishlistItems.cartItems.update((currentItems) => {
			const remaining = Object.entries(currentItems).filter(([, item]) =>
				activeGroupIds.has(item.groupId as string)
			);

			if (remaining.length === Object.keys(currentItems).length) {
				return currentItems;
			}

			return Object.fromEntries(remaining);
		});

		if (!activeGroupIds.size) {
			wishlistDrawer.states.open.set(false);
		}
	};

	const syncActiveDrops = (
		activeGroupIds: Set<string>,
		hasEligibleDrop: boolean
	) => {
		pruneInactiveDrops(activeGroupIds);
		showWishlistIcon.set(hasEligibleDrop);
	};

	const isInWishlist = (productId: string | null | undefined) =>
		!!productId && !!get(wishlistItems.cartItems)[productId];

	return {
		wishlistItems,
		wishlistDrawer,
		showWishlistIcon,
		pruneInactiveDrops,
		syncActiveDrops,
		isInWishlist,
	};
};
